/**
 * Treatment selection as the clinic app applies it while the user picks (inventory.md 4.6, C-42): at most five,
 * only wells with a reaction, never a control, and environmental antigens never mixed with other groups. The
 * server validates the same rules on every order (Q-10); this only keeps the screen honest as the user taps.
 */
import { isEnvironmental } from "./tray";

export const MAX_COMPONENTS = 5;

export interface Candidate {
  antigenId: string;
  position: number;
  /** Reading in millimetres; null when the well was not read. */
  reading: number | null;
  isControl: boolean;
}

export type Selectable = { ok: true } | { ok: false; reason: "control" | "no_reaction" | "not_read" | "full" };

/** Whether `candidate` could be added to `selected` now, and why not otherwise. Environmental mixing is handled by `toggle`, which swaps groups. */
export function selectable(candidate: Candidate, selected: readonly string[]): Selectable {
  if (candidate.isControl) return { ok: false, reason: "control" };
  if (candidate.reading === null) return { ok: false, reason: "not_read" };
  if (candidate.reading <= 0) return { ok: false, reason: "no_reaction" };
  if (!selected.includes(candidate.antigenId) && selected.length >= MAX_COMPONENTS) return { ok: false, reason: "full" };
  return { ok: true };
}

/**
 * Adds or removes one antigen. Picking an environmental antigen clears every non-environmental pick and the
 * other way round, as the installed app did; an unselectable candidate leaves the selection unchanged.
 */
export function toggle(selected: readonly string[], candidate: Candidate, byId: ReadonlyMap<string, Candidate>): string[] {
  if (selected.includes(candidate.antigenId)) return selected.filter((id) => id !== candidate.antigenId);
  const env = isEnvironmental(candidate.position);
  const kept = selected.filter((id) => {
    const c = byId.get(id);
    return c ? isEnvironmental(c.position) === env : false;
  });
  const check = selectable(candidate, kept);
  if (!check.ok) return [...selected];
  return [...kept, candidate.antigenId];
}

/** Every rule violation of a finished selection, for the order button and its explanation. */
export function violations(selected: readonly string[], byId: ReadonlyMap<string, Candidate>): string[] {
  const out: string[] = [];
  if (selected.length === 0) out.push("Pick at least one antigen.");
  if (selected.length > MAX_COMPONENTS) out.push(`At most ${MAX_COMPONENTS} antigens can be ordered.`);
  const picked = selected.map((id) => byId.get(id)).filter((c): c is Candidate => !!c);
  if (picked.some((c) => c.isControl)) out.push("Controls are never part of an order.");
  if (picked.some((c) => c.reading === null || c.reading <= 0)) out.push("Only wells with a reaction can be ordered.");
  const env = picked.filter((c) => isEnvironmental(c.position)).length;
  if (env > 0 && env < picked.length) out.push("Environmental antigens cannot be mixed with other groups.");
  return out;
}
